import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, KeyRound, UserPlus, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  { value: "admin", label: "Admin (full åtkomst)" },
  { value: "manager", label: "Restaurangchef" },
  { value: "developer", label: "Utvecklare" },
  { value: "menu_editor", label: "Meny-redigerare (meny + statistik)" },
  { value: "analyst", label: "Statistik (endast statistik)" },
];

function formatError(detail) {
  if (!detail) return "Något gick fel";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function AddUserModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "manager" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Lösenord måste vara minst 8 tecken");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/users", form);
      toast.success("Användare tillagd");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <form
        onSubmit={submit}
        data-testid="user-modal"
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden my-8"
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
          <h3 className="font-serif-display text-2xl font-bold">Lägg till användare</h3>
          <button type="button" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--surface-alt)]">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">E-post</span>
            <input
              required
              type="email"
              data-testid="user-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="namn@monami.se"
              className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Namn</span>
            <input
              data-testid="user-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Anna Andersson"
              className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Roll</span>
            <select
              data-testid="user-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
            >
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Lösenord (min 8 tecken)</span>
            <input
              required
              type="password"
              data-testid="user-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              minLength={8}
              className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 p-6 bg-[var(--surface-alt)] border-t border-[var(--border)]">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 font-semibold border border-[var(--border)] bg-white hover:bg-stone-50">
            Avbryt
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="user-save"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white disabled:opacity-60"
          >
            <Save size={16} /> {saving ? "Sparar…" : "Lägg till"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ResetPasswordModal({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("Lösenord måste vara minst 8 tecken");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/auth/users/${user.id}/password`, { new_password: pw });
      toast.success(`Lösenord uppdaterat för ${user.email}`);
      onClose();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="user-reset-modal">
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
          <h3 className="font-serif-display text-2xl font-bold">Återställ lösenord</h3>
          <button type="button" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--surface-alt)]">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-[var(--text-muted)]">Sätt ett nytt lösenord för <strong>{user.email}</strong>.</p>
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Nytt lösenord (min 8 tecken)</span>
            <input
              type="password"
              required
              minLength={8}
              data-testid="user-reset-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 p-6 bg-[var(--surface-alt)] border-t border-[var(--border)]">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 font-semibold border border-[var(--border)] bg-white hover:bg-stone-50">
            Avbryt
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="user-reset-save"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white disabled:opacity-60"
          >
            <Save size={16} /> {saving ? "Sparar…" : "Spara nytt lösenord"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ChangeOwnPasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("Nytt lösenord måste vara minst 8 tecken");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Lösenorden matchar inte");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Ditt lösenord har uppdaterats");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="change-password-form"
      className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden"
    >
      <div className="p-6 border-b border-[var(--border)]">
        <h3 className="font-serif-display text-2xl font-bold flex items-center gap-2">
          <KeyRound size={20} /> Byt mitt lösenord
        </h3>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Ändra ditt eget inloggningslösenord. Du behöver veta ditt nuvarande lösenord.
        </p>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Nuvarande lösenord</span>
          <input
            required
            type="password"
            data-testid="current-password"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Nytt lösenord</span>
          <input
            required
            type="password"
            minLength={8}
            data-testid="new-password"
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Bekräfta nytt lösenord</span>
          <input
            required
            type="password"
            minLength={8}
            data-testid="confirm-password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
          />
        </label>
      </div>
      <div className="flex justify-end p-4 bg-[var(--surface-alt)] border-t border-[var(--border)]">
        <button
          type="submit"
          disabled={saving}
          data-testid="change-password-save"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white disabled:opacity-50"
        >
          <Save size={15} /> {saving ? "Sparar…" : "Uppdatera lösenord"}
        </button>
      </div>
    </form>
  );
}

const ROLE_BADGE = {
  admin: "bg-amber-50 text-amber-800 border-amber-200",
  manager: "bg-sky-50 text-sky-800 border-sky-200",
  developer: "bg-violet-50 text-violet-800 border-violet-200",
  analyst: "bg-emerald-50 text-emerald-800 border-emerald-200",
  menu_editor: "bg-orange-50 text-orange-800 border-orange-200",
};

export default function UsersEditor() {
  const { user: me } = useAuth();
  const canManageUsers = me?.role === "admin" || me?.role === "developer";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(canManageUsers);
  const [showAdd, setShowAdd] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null); // {id, email, role, name} being inline-edited

  const load = async () => {
    if (!canManageUsers) return;
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data || []);
    } catch (err) {
      toast.error("Kunde inte ladda användare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [canManageUsers]);

  const remove = async (u) => {
    if (!window.confirm(`Ta bort ${u.email}?`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success("Borttagen");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const saveEdit = async () => {
    if (!editingUser) return;
    try {
      await api.put(`/auth/users/${editingUser.id}`, {
        role: editingUser.role,
        name: editingUser.name,
        email: editingUser.email,
      });
      toast.success("Användare uppdaterad");
      setEditingUser(null);
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-black">{canManageUsers ? "Användare" : "Mitt konto"}</h1>
        <p className="text-[var(--text-muted)] mt-1">
          {canManageUsers
            ? "Hantera vem som har åtkomst till adminpanelen — restaurangchef, utvecklare och fler administratörer."
            : "Hantera ditt eget konto och byt lösenord."}
        </p>
      </div>

      <ChangeOwnPasswordCard />

      {canManageUsers && (
      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
          <div>
            <h3 className="font-serif-display text-2xl font-bold flex items-center gap-2">
              <ShieldCheck size={20} /> Konton
            </h3>
            <p className="text-sm text-[var(--text-muted)] mt-1">{users.length} användare totalt · dubbelklicka på en rad för att redigera</p>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="users-add-button"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-4 py-2 text-sm font-semibold"
          >
            <UserPlus size={15} /> Lägg till användare
          </button>
        </div>
        {loading ? (
          <div className="py-12 text-center text-[var(--text-muted)]">Laddar…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-alt)] text-xs uppercase tracking-wider text-[var(--text-muted)]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">E-post</th>
                <th className="text-left px-5 py-3 font-semibold hidden sm:table-cell">Namn</th>
                <th className="text-left px-5 py-3 font-semibold">Roll</th>
                <th className="text-right px-5 py-3 font-semibold">Åtgärder</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {users.map((u) => {
                const isMe = u.id === me?.id;
                const isEditing = editingUser?.id === u.id;
                if (isEditing) {
                  return (
                    <tr key={u.id} data-testid={`user-row-editing-${u.id}`} className="bg-[var(--primary)]/5">
                      <td className="px-5 py-3">
                        <input
                          type="email"
                          data-testid={`user-edit-email-${u.id}`}
                          value={editingUser.email}
                          onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                          className="w-full bg-white border border-[var(--border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--primary)]"
                        />
                      </td>
                      <td className="px-5 py-3 hidden sm:table-cell">
                        <input
                          data-testid={`user-edit-name-${u.id}`}
                          value={editingUser.name}
                          onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                          className="w-full bg-white border border-[var(--border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--primary)]"
                        />
                      </td>
                      <td className="px-5 py-3">
                        <select
                          data-testid={`user-edit-role-${u.id}`}
                          value={editingUser.role}
                          onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                          className="w-full bg-white border border-[var(--border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--primary)]"
                        >
                          {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={saveEdit}
                            data-testid={`user-save-${u.id}`}
                            className="rounded-full px-3 py-1.5 text-xs font-semibold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                          >
                            Spara
                          </button>
                          <button
                            onClick={() => setEditingUser(null)}
                            className="rounded-full px-3 py-1.5 text-xs font-semibold bg-white border border-[var(--border)]"
                          >
                            Avbryt
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    onDoubleClick={() => setEditingUser({ id: u.id, email: u.email, name: u.name || "", role: u.role })}
                    className="cursor-pointer hover:bg-[var(--surface-alt)]/50"
                  >
                    <td className="px-5 py-4">
                      <span className="font-semibold">{u.email}</span>
                      {isMe && <span className="ml-2 text-xs rounded-full bg-stone-100 px-2 py-0.5">Du</span>}
                    </td>
                    <td className="px-5 py-4 hidden sm:table-cell text-[var(--text-muted)]">{u.name || "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${ROLE_BADGE[u.role] || "bg-stone-50 border-stone-200"}`}>
                        {ROLES.find((r) => r.value === u.role)?.label?.split(" ")[0] || u.role}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => setEditingUser({ id: u.id, email: u.email, name: u.name || "", role: u.role })}
                          data-testid={`user-edit-${u.id}`}
                          className="rounded-full px-3 py-1.5 text-xs font-semibold bg-white border border-[var(--border)] hover:border-[var(--text)]"
                        >
                          Redigera
                        </button>
                        <button
                          onClick={() => setResetUser(u)}
                          data-testid={`user-reset-${u.id}`}
                          className="rounded-full px-3 py-1.5 text-xs font-semibold bg-white border border-[var(--border)] hover:border-[var(--text)]"
                        >
                          Byt lösenord
                        </button>
                        {!isMe && (
                          <button
                            onClick={() => remove(u)}
                            data-testid={`user-delete-${u.id}`}
                            className="w-8 h-8 grid place-items-center rounded-full hover:bg-red-50 hover:text-red-600"
                            title="Ta bort"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-[var(--text-muted)]">Inga användare ännu.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      )}

      {canManageUsers && showAdd && <AddUserModal onClose={() => setShowAdd(false)} onSaved={load} />}
      {canManageUsers && resetUser && <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />}
    </div>
  );
}
